import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import SecuritySection from "@/components/SecuritySection";
import ExcludedIpsSection from "@/components/ExcludedIpsSection";
import DedupSection from "@/components/DedupSection";
import { CheckCircle2, XCircle, AlertTriangle, Link2, Copy } from "lucide-react";

export default function SettingsPage() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/ms365/status");
    setStatus(data);
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(location.search);
    if (params.get("ms") === "connected") {
      toast.success("Microsoft 365 collegato correttamente");
      refresh();
    } else if (params.get("ms") === "error") {
      toast.error("Errore di connessione: " + (params.get("msg") || "sconosciuto"));
    }
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/ms365/authorize");
      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare l'account Microsoft 365?")) return;
    await api.post("/ms365/disconnect");
    toast.success("Account scollegato");
    load();
    refresh();
  };

  const copyRedirect = () => {
    if (!status?.redirect_uri) return;
    navigator.clipboard.writeText(status.redirect_uri);
    toast.success("URI copiato");
  };

  return (
    <>
      <PageHeader
        title="Impostazioni"
        subtitle="Gestisci la connessione con Microsoft 365 e le preferenze account."
        testid="settings-header"
      />
      <PageBody>
        <div className="max-w-3xl space-y-6">
          <div className="border border-border bg-white p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-heading text-xl">Microsoft 365</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Le newsletter vengono spedite direttamente dal tuo account M365 tramite Microsoft Graph.
                </p>
              </div>
              {status?.connected ? (
                <CheckCircle2 className="w-6 h-6 text-primary" strokeWidth={1.5} />
              ) : (
                <XCircle className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
              )}
            </div>

            {!status?.configured && (
              <div className="border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-destructive shrink-0" strokeWidth={1.5} />
                <div className="text-sm">
                  <div className="font-medium text-destructive">
                    Credenziali Azure non ancora configurate
                  </div>
                  <div className="text-muted-foreground mt-1">
                    Per abilitare l'invio, un amministratore deve registrare un'app in Azure e
                    inserire <code className="bg-muted px-1">AZURE_CLIENT_ID</code>,
                    {" "}<code className="bg-muted px-1">AZURE_CLIENT_SECRET</code> e
                    {" "}<code className="bg-muted px-1">AZURE_TENANT_ID</code> nel file{" "}
                    <code className="bg-muted px-1">backend/.env</code>.
                  </div>
                </div>
              </div>
            )}

            {status?.connected ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Account collegato
                    </div>
                    <div className="mt-1 font-medium" data-testid="ms-account-email">
                      {status.email}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      Utente
                    </div>
                    <div className="mt-1 font-medium">{status.display_name || "—"}</div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={disconnect}
                  data-testid="ms-disconnect-btn"
                >
                  Scollega account
                </Button>
              </div>
            ) : (
              <Button
                onClick={connect}
                disabled={!status?.configured || connecting}
                data-testid="ms-connect-btn"
              >
                <Link2 className="w-4 h-4 mr-2" />
                {connecting ? "Reindirizzamento…" : "Collega Microsoft 365"}
              </Button>
            )}
          </div>

          <div className="border border-border bg-white p-6">
            <h2 className="font-heading text-xl mb-2">Setup Azure (per amministratori)</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Registra un'app nel portale{" "}
              <a
                href="https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
                target="_blank"
                rel="noreferrer"
                className="text-primary underline"
              >
                Microsoft Entra
              </a>{" "}
              con:
            </p>
            <ul className="text-sm space-y-2 list-disc pl-5 mb-4">
              <li>
                Tipo <strong>Web</strong> con questo <strong>Redirect URI</strong>:
              </li>
            </ul>
            <div className="flex items-center gap-2 bg-muted/50 border border-border p-3 rounded-sm">
              <code className="text-xs flex-1 break-all" data-testid="redirect-uri-value">
                {status?.redirect_uri || "—"}
              </code>
              <Button size="icon" variant="ghost" onClick={copyRedirect} data-testid="copy-redirect">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <ul className="text-sm space-y-2 list-disc pl-5 mt-4">
              <li>API permissions (delegate): <code className="bg-muted px-1">Mail.Send</code>, <code className="bg-muted px-1">offline_access</code>, <code className="bg-muted px-1">User.Read</code>, <code className="bg-muted px-1">openid</code>, <code className="bg-muted px-1">profile</code></li>
              <li>Crea un <strong>Client Secret</strong> in "Certificates &amp; secrets"</li>
              <li>Inserisci Client ID, Secret e Tenant ID in <code className="bg-muted px-1">backend/.env</code></li>
            </ul>
          </div>

          <div className="border border-border bg-white p-6">
            <h2 className="font-heading text-xl mb-2">Account</h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Email</div>
                <div className="mt-1 font-medium">{user?.email}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Nome</div>
                <div className="mt-1 font-medium">{user?.name || "—"}</div>
              </div>
            </div>
          </div>

          <SecuritySection />

          <ExcludedIpsSection />

          <DedupSection />
        </div>
      </PageBody>
    </>
  );
}
